import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Currency,
  readCurrencyCookieClient,
  readStoredCurrency,
  writeCurrencyCookie,
  writeStoredCurrency,
} from "@/utils/currency";
import { trackCurrencyChange } from "@/utils/analytics";

const CurrencyContext = createContext<{
  currency: Currency | null;
  setCurrency: (currency: Currency) => void;
} | null>(null);

export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [currency, setCurrencyState] = useState<Currency | null>(null);

  useEffect(() => {
    const stored = readStoredCurrency() ?? readCurrencyCookieClient();
    if (stored) setCurrencyState(stored);
  }, []);

  const setCurrency = (next: Currency) => {
    trackCurrencyChange(next.code || next.id || "IQD");
    setCurrencyState(next);
    writeStoredCurrency(next);
    writeCurrencyCookie(next);
    queryClient.invalidateQueries();
  };

  const value = useMemo(() => ({ currency, setCurrency }), [currency]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
};
